import { useCallback, useRef, useState, useEffect } from 'react';
import { AbsoluteCenter, Spinner, useToast } from '@chakra-ui/react';
import { t } from 'i18next';
import Cropper from 'react-perspective-cropper';
import type { CropperRef, CropperState } from 'react-perspective-cropper';
import { useFilePicker } from 'use-file-picker';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ToastMessageBox from '../../../components/alerts/ToastMessageBox';
import StickyHeader from '../../components/bar/StickyHeader';
import { ReactComponent as ArrowLeftIcon } from '../../../icons/arrow.left.svg';
import { ReactComponent as RefreshFillIcon } from '../../../icons/refresh.fill.svg';
import { Button } from '@peacecover/component';

// OpenCV 필터 기본 설정
const DEFAULT_CV_PARAMS = {
  grayScale: false,
  th: false
};

// 타입 정의들
interface ImageCropSavePageProps {
  setImageData: (dataURL: string) => void;
  onComplete?: () => void;
  onCancel?: () => void;
  beforeUploadContent: BeforeUploadContent;
  skipIntro?: boolean;
  initialImageData?: string;
}

interface RenderBeforeUploadProps {
  handleAddPhotoClick: () => void;
  handleCancel: () => void;
  beforeUploadContent: BeforeUploadContent;
}

interface BeforeUploadContent {
  imageSrc?: string;
  title: string;
  description: string;
  disclaimerRowLine: string[];
  bottomDescription?: string;
}

interface RenderCropperProps {
  cropperRef: React.RefObject<CropperRef>;
  imageToCrop: string | null;
  dataURL: string | null;
  setDataURL: (dataURL: string | null) => void;
  resetImage: () => void;
  onCancel: () => void;
  onChange: (state: CropperState) => void;
  onDragStop: (state: CropperState) => void;
  handleComplete: () => void;
  doCrop: () => Promise<void>;
}

/**
 * 이미지 크롭 저장 컴포넌트, setImageData에 크롭된 이미지 데이터를 전달
 * @param setImageData - 크롭된 이미지 데이터를 상위 컴포넌트로 전달하는 함수
 * @param onComplete - 이미지 선택 완료 시 호출되는 콜백 함수
 * @param onCancel - 취소 시 호출되는 콜백 함수 (없을 경우 뒤로가기)
 * @param beforeUploadContent - 이전 페이지에서 전달된 이미지 검색 전 컨텐츠(imageSrc 없을 경우 카메라이미지 사용)
 */
export default function ImageCropSavePage({
  setImageData,
  onComplete,
  onCancel,
  beforeUploadContent,
  skipIntro = false,
  initialImageData
}: ImageCropSavePageProps): JSX.Element {
  const toast = useToast();
  const navigate = useNavigate();

  // 크롭 관련 상태들
  const cropperRef = useRef<CropperRef>(null);
  const [_cropState, setCropState] = useState<CropperState | undefined>();
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [dataURL, setDataURL] = useState<string | null>(null);
  const [isOpenCVLoading, setIsOpenCVLoading] = useState<boolean>(false);

  // Blob을 DataURL로 변환하는 유틸리티 함수
  const convertBlobToDataURL = (blob: Blob): void => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setDataURL(reader.result as string);
    };
    reader.readAsDataURL(blob);
  };

  // 크롭 이벤트 핸들러들
  const onDragStop = useCallback((s: CropperState) => setCropState(s), []);
  const onChange = useCallback((s: CropperState) => setCropState(s), []);

  // 파일 선택기 설정
  const { openFilePicker, filesContent, clear } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false
  });

  const handleAddPhotoClick = (): void => {
    openFilePicker();
  };

  useEffect(() => {
    if (initialImageData) {
      setImageToCrop(initialImageData);
    } else if (skipIntro) {
      openFilePicker();
    }
  }, [initialImageData, skipIntro, openFilePicker]);

  // 파일 선택 시 OpenCV 로딩 및 이미지 설정
  useEffect(() => {
    if (filesContent.length > 0) {
      setIsOpenCVLoading(true);
      const interval = setInterval(() => {
        // @ts-ignore
        if (window.cv) {
          setIsOpenCVLoading(false);
          clearInterval(interval);
        }
      }, 100);
      setImageToCrop(filesContent[0].content);
    }
  }, [filesContent]);

  // 이미지 크롭 실행 함수
  const doCrop = async (): Promise<void> => {
    try {
      const res = await cropperRef.current?.done({
        preview: true,
        filterCvParams: DEFAULT_CV_PARAMS
      });
      if (res) {
        convertBlobToDataURL(res);
      }
    } catch (_e) {
      resetImage();
      toast({
        duration: 1500,
        render: () => <ToastMessageBox message={t('errorBoundary.title')} />
      });
    }
  };

  // 이미지 초기화 함수, 이미지 선택화면으로 이동
  const resetImage = (): void => {
    clear();
    setImageToCrop(null);
    setDataURL(null);
  };

  // 이미지 데이터를 상위 컴포넌트로 전달하는 함수
  const handleComplete = (): void => {
    if (dataURL) {
      setImageData(dataURL);
      if (onComplete) {
        onComplete();
      }
    }
  };

  // 이미지가 선택되었는지 확인하는 함수
  const isSelectedImageData: boolean = !!(imageToCrop || dataURL);

  const handleCancel = (): void => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      {isSelectedImageData ? (
        <RenderCropper
          cropperRef={cropperRef}
          imageToCrop={imageToCrop}
          dataURL={dataURL}
          setDataURL={setDataURL}
          resetImage={resetImage}
          onCancel={handleCancel}
          onChange={onChange}
          onDragStop={onDragStop}
          handleComplete={handleComplete}
          doCrop={doCrop}
        />
      ) : (
        <RenderBeforeUpload
          handleAddPhotoClick={handleAddPhotoClick}
          handleCancel={handleCancel}
          beforeUploadContent={beforeUploadContent}
        />
      )}

      {/* OpenCV 로딩 스피너 */}
      {isOpenCVLoading && (
        <AbsoluteCenter zIndex={9999}>
          <Spinner size="lg" className="text-primary-light dark:text-primary-dark" />
        </AbsoluteCenter>
      )}
    </>
  );
}

const RenderBeforeUpload = ({
  handleAddPhotoClick,
  handleCancel,
  beforeUploadContent
}: RenderBeforeUploadProps): JSX.Element => {
  const emoji = ['💁‍♂️', '🗂️', '✨'];
  const pageTitle = t('reportPhotoCardPage.pageName');
  return (
    <div className="relative flex min-h-screen w-full flex-col bg-pure-black">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <header className="sticky top-0 z-[1000] flex shrink-0 items-center justify-center bg-transparent px-[16px] pb-[15px] pt-[calc(16px+env(safe-area-inset-top))]">
        <button
          type="button"
          className="absolute left-[16px] flex items-center"
          onClick={handleCancel}
        >
          <ArrowLeftIcon className="fill-white" />
        </button>
        <h1 className="pointer-events-none text-center text-[17px] font-bold leading-[20px] text-white">
          {pageTitle}
        </h1>
      </header>

      <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-[24px] pb-[calc(24px+env(safe-area-inset-bottom))]">
        <div className="flex w-full max-w-[var(--chakra-sizes-xl)] flex-col items-center">
          {/* 제목 */}
          <div className="text-[26px] font-bold leading-[31px] text-center text-white whitespace-pre-wrap">
            {beforeUploadContent.title}
          </div>

          {/* 설명 */}
          <div className="text-[15px] font-medium leading-[18px] text-center pt-[14px] text-text-main-lv4 whitespace-pre-wrap mb-[17px]">
            {beforeUploadContent.description}
          </div>

          {/* 카메라 아이콘 */}
          <img
            width={'158px'}
            src={beforeUploadContent.imageSrc || '/assets/icons/UploadPhotoCardCamera.png'}
            alt="UploadPhotoCardCamera"
          />

          {/* 주의사항 박스 */}
          <div className="mt-[24px] w-full px-0">
            <div className="flex flex-col gap-[16px] rounded-[23px] bg-[rgba(255,255,255,0.05)] p-[24px]">
              {beforeUploadContent.disclaimerRowLine.map((line, index) => (
                <div key={line} className="flex flex-row gap-[8px]">
                  <div className="text-[16px] font-semibold leading-[19px] text-center">
                    {emoji[index % emoji.length]}
                  </div>
                  <div className="text-[13px] font-normal leading-[15px] text-left text-white">
                    {line}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 하단 설명 */}
          {beforeUploadContent.bottomDescription && (
            <div className="mt-[24px] text-center text-[12px] font-normal text-white">
              {t('imageSearch.beforeUpload.bottomDescription')}
            </div>
          )}

          {/* 사진 추가 버튼 */}
          <div className="mt-[24px] w-full">
            <Button
              size="large"
              className="w-full text-[17px] font-semibold"
              onClick={handleAddPhotoClick}
            >
              {t('ReportPhotoCardUploadPhotoPage.addPhotoCardButton')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 이미지 선택후 크롭퍼 컴포넌트
const RenderCropper = ({
  cropperRef,
  imageToCrop,
  dataURL,
  setDataURL,
  resetImage,
  onCancel,
  onChange,
  onDragStop,
  handleComplete,
  doCrop
}: RenderCropperProps): JSX.Element => {
  const handleBack = (): void => {
    if (dataURL) {
      cropperRef.current?.backToCrop();
      setDataURL(null);
    } else {
      onCancel();
    }
  };

return (
  <div className="w-screen h-screen">
    <StickyHeader
      transparent={true}
      leftContent={<ArrowLeftIcon className="fill-white w-[24px] h-[24px]" onClick={handleBack} />}
      // @TODO : 이미지 회전하는 기능 추가
      // rightContent={
      //   dataURL && (
      //     <RefreshFillIcon
      //       className="fill-white w-[24px] h-[24px] cursor-pointer"
      //       onClick={이미지회전하기}
      //     />
      //   )
      // }
    />

    {/* 오버레이 배경 */}
    <div className="absolute inset-0 z-0 bg-black bg-opacity-[0.45]">
      {/* 크롭 영역 - 헤더와 하단 UI 사이 공간으로 제한 */}
      <div
        className="flex justify-center items-center px-4"
        style={{
          height: 'calc(100vh - 60px - 15vh)',
          paddingTop: '80px' // 헤더 아래부터 시작
        }}
      >
        <div className="z-[1001] rounded-[7px] bg-white max-w-full max-h-full flex justify-center items-center">
          {/* @ts-ignore */}
          <Cropper
            ref={cropperRef}
            image={imageToCrop}
            onChange={onChange}
            onDragStop={onDragStop}
            resetImage={resetImage}
            pointBgColor="#FFFFFF"
            pointSize={16}
            maxWidth={Math.min(window.innerWidth * 0.9)}
            maxHeight={Math.min(window.innerHeight * 0.65)}
            pointBorder=""
            lineColor="#FFFFFF"
            openCvPath="/assets/opencv/opencv-3-4-13.min.js"
          />
        </div>
      </div>

      {/* 안내 텍스트와 버튼을 위한 하단 래퍼 */}
      <div className="absolute inset-x-0 bottom-0 z-[99999] flex flex-col items-center">
        <div className="flex w-full max-w-[var(--chakra-sizes-xl)] flex-col items-center">
          {/* 안내 텍스트 */}
          <div className="mb-[3vh] flex justify-center items-center">
            <p className="text-[15px] font-normal text-white">
              {t('imageSearch.imageCropSave.confirmDescription')}
            </p>
          </div>

          {/* 확인 버튼 */}
          <div className="relative h-[103px] w-full">
            <div className="absolute inset-0 bg-white bg-opacity-60" />
            <div className="absolute inset-0 bg-black bg-opacity-30 mix-blend-lighten" />
            <div className="relative z-10 flex h-full w-full items-center justify-center px-[24px]">
              <Button
                size="large"
                className="w-full text-[17px] font-semibold"
                onClick={dataURL ? handleComplete : doCrop}
              >
                {dataURL ? t('search') : t('confirm')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);
};
